import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {KTIcon} from '../../../../../../_metronic/helpers'
import {getClientRouting} from '../../../core/_requests'
import {
  ClientRouting as ClientRoutingData,
  ClientRoutingAdapter,
  ClientRoutingCashbox,
  ClientRoutingDirection,
  ClientRoutingGroup,
} from '../../../core/_rest_models'
import {
  currencySymbol,
  flagColor,
  pluralize,
  ROUTING_DIRECTION_HINT,
  ROUTING_DIRECTION_TITLE,
  ROUTING_DISABLED_REASON,
  ROUTING_WARNING_SEVERITY,
  ROUTING_WARNING_TEXT,
  SOURCE_LABEL,
} from '../../../core/routing.consts'
import { getErrorText } from '../../../../../common/api/errors'

const LOAD_ERROR = 'Не удалось загрузить маршрутизацию'

const LOCAL_ADAPTER_TITLE = 'свои кассы'

const amountLabel = (adapter: ClientRoutingAdapter, currency: string): string => {
  const symbol = currencySymbol(currency)
  const parts: string[] = []

  if (adapter.minDeposit > 0) parts.push(`от ${adapter.minDeposit.toLocaleString('ru-RU')} ${symbol}`)
  if (adapter.maxDeposit > 0) parts.push(`до ${adapter.maxDeposit.toLocaleString('ru-RU')} ${symbol}`)

  return parts.join(' · ')
}

/** Метод показываем с валютой и источником только там, где он внутри направления не один. */
const ambiguousMethods = (groups: ClientRoutingGroup[]): Set<string> => {
  const counters = new Map<string, number>()
  groups.forEach((g) => counters.set(g.method, (counters.get(g.method) ?? 0) + 1))

  const result = new Set<string>()
  counters.forEach((count, method) => {
    if (count > 1) result.add(method)
  })

  return result
}

const FlagBadge: React.FC<{flag: string}> = ({flag}) => {
  const intl = useIntl()
  const color = flagColor(flag)
  const label = intl.formatMessage({id: 'client.flag.' + flag, defaultMessage: flag})

  return (
    <span className='badge badge-light d-inline-flex align-items-center'>
      <span
        className='d-inline-block rounded-circle me-2 flex-shrink-0'
        style={{
          width: 9,
          height: 9,
          backgroundColor: color ?? '#a1a5b7',
          border: color === '#ffffff' ? '1px solid #d9d9d9' : undefined,
        }}
      />
      {label}
    </span>
  )
}

const CashboxList: React.FC<{cashboxes: ClientRoutingCashbox[]}> = ({cashboxes}) => {
  if (cashboxes.length === 0) {
    return <div className='text-danger fs-8'>рабочих касс по флагу нет</div>
  }

  return (
    <div className='d-flex flex-wrap gap-2'>
      {cashboxes.map((cashbox) => (
        <span
          key={`${cashbox.priority}-${cashbox.title}`}
          className={`badge badge-light-${cashbox.enabled ? 'success' : 'secondary'} text-truncate`}
          style={{maxWidth: 260}}
        >
          {cashbox.priority} · {cashbox.title}
          {cashbox.blocked && <span className='ms-2 text-danger'>заблокирована</span>}
          {cashbox.cashboxDisabled && cashbox.enabled && (
            <span className='ms-2 text-warning'>касса выключена, но выплату примет</span>
          )}
        </span>
      ))}
    </div>
  )
}

const AdapterRow: React.FC<{
  adapter: ClientRoutingAdapter
  currency: string
  isFirstWorking: boolean
}> = ({adapter, currency, isFirstWorking}) => {
  const amount = amountLabel(adapter, currency)
  const title = adapter.isLocal ? `${adapter.name} (${LOCAL_ADAPTER_TITLE})` : adapter.name

  return (
    <div
      className={`d-flex align-items-center py-2 border-bottom ${adapter.enabled ? '' : 'bg-light-secondary'}`}
      style={{gap: 12}}
    >
      <span className='text-gray-500 text-end flex-shrink-0' style={{width: 28}}>
        {adapter.priority}
      </span>

      <span className='flex-grow-1 text-truncate' style={{minWidth: 0}}>
        <span className={adapter.enabled ? 'fw-bold' : 'text-gray-600'}>{title}</span>
        {isFirstWorking && (
          <span className='badge badge-light-primary ms-2'>сработает первым</span>
        )}
        {!adapter.enabled && adapter.disabledReason && (
          <span className='text-danger fs-8 ms-2'>{ROUTING_DISABLED_REASON[adapter.disabledReason]}</span>
        )}
      </span>

      <span className='text-muted fs-8 text-end flex-shrink-0' style={{width: 200}}>
        {amount}
      </span>
    </div>
  )
}

const GroupCard: React.FC<{group: ClientRoutingGroup; ambiguous: boolean}> = ({
  group,
  ambiguous,
}) => {
  const [expanded, setExpanded] = useState(false)
  const [showDisabled, setShowDisabled] = useState(false)

  const working = group.adapters.filter((a) => a.enabled)
  const disabled = group.adapters.filter((a) => !a.enabled)
  const firstWorking = working[0]
  const localWithCashboxes = group.adapters.find((a) => a.isLocal && a.cashboxes !== null)
  const visible = showDisabled ? group.adapters : working

  const title = ambiguous
    ? `${group.method} · ${group.currency} · ${SOURCE_LABEL[group.source as 'MERCHANT' | 'PLATFORM'] ?? group.source}`
    : group.method

  return (
    <div className='border rounded mb-3'>
      <div
        className='d-flex align-items-center flex-wrap p-4 cursor-pointer'
        style={{gap: 8}}
        role='button'
        onClick={() => setExpanded((v) => !v)}
      >
        <KTIcon iconName={expanded ? 'down' : 'right'} className='fs-4 text-gray-500' />
        <span className='fw-bold me-2'>{title}</span>

        {firstWorking ? (
          <span className='text-muted fs-7'>
            первым: {firstWorking.isLocal ? LOCAL_ADAPTER_TITLE : firstWorking.name}
          </span>
        ) : (
          <span className='text-danger fs-7'>рабочих адаптеров нет</span>
        )}

        <span className='badge badge-light ms-auto'>
          рабочих {working.length} из {group.adapters.length}
        </span>

        {group.fallbackToWhite && <span className='badge badge-light-info'>конфигурация white</span>}

        {group.warnings.map((warning) => (
          <span key={warning} className={`badge badge-light-${ROUTING_WARNING_SEVERITY[warning]}`}>
            !
          </span>
        ))}
      </div>

      {expanded && (
        <div className='px-4 pb-4'>
          {group.fallbackToWhite && (
            <div className='alert alert-info py-2 px-3 fs-7'>
              {group.ownDisabledCount > 0
                ? `Правила флага заведены, но все выключены (${group.ownDisabledCount}) — работает конфигурация white`
                : 'Правил для этого флага нет — работает конфигурация white'}
            </div>
          )}

          {group.warnings.map((warning) => (
            <div
              key={warning}
              className={`alert alert-${ROUTING_WARNING_SEVERITY[warning]} py-2 px-3 fs-7`}
            >
              {ROUTING_WARNING_TEXT[warning]}
            </div>
          ))}

          <div className='mb-3'>
            {visible.map((adapter) => (
              <AdapterRow
                key={adapter.name}
                adapter={adapter}
                currency={group.currency}
                isFirstWorking={adapter.name === firstWorking?.name}
              />
            ))}
          </div>

          {disabled.length > 0 && (
            <button
              type='button'
              className='btn btn-sm btn-light-secondary mb-3'
              onClick={() => setShowDisabled((v) => !v)}
            >
              {showDisabled
                ? 'Скрыть выключенные'
                : `Показать выключенные (${disabled.length})`}
            </button>
          )}

          {localWithCashboxes && (
            <div>
              <div className='text-gray-500 text-uppercase fs-8 fw-bold mb-2'>
                Кассы по флагу (используются, когда цепочка доходит до своих касс)
              </div>
              <CashboxList cashboxes={localWithCashboxes.cashboxes ?? []} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const DirectionBlock: React.FC<{direction: ClientRoutingDirection; flag: string}> = ({
  direction,
  flag,
}) => {
  const intl = useIntl()
  const ambiguous = useMemo(() => ambiguousMethods(direction.groups), [direction.groups])
  const flagLabel = intl.formatMessage({id: 'client.flag.' + flag, defaultMessage: flag})

  return (
    <div className='mb-8'>
      <div className='d-flex align-items-baseline mb-3' style={{gap: 8}}>
        <span className='fw-bold fs-5'>{ROUTING_DIRECTION_TITLE[direction.direction]}</span>
        <span className='text-muted fs-8'>{ROUTING_DIRECTION_HINT[direction.direction]}</span>
        {direction.groups.length > 0 && (
          <span className='badge badge-light ms-auto'>
            {direction.groups.length}{' '}
            {pluralize(direction.groups.length, 'цепочка', 'цепочки', 'цепочек')}
          </span>
        )}
      </div>

      {direction.groups.length === 0 ? (
        <div className='text-muted'>Включённых правил для флага «{flagLabel}» нет</div>
      ) : (
        direction.groups.map((group) => (
          <GroupCard
            key={`${group.method}-${group.currency}-${group.source}`}
            group={group}
            ambiguous={ambiguous.has(group.method)}
          />
        ))
      )}

      {direction.disabledMethods.length > 0 && (
        <div className='d-flex flex-wrap align-items-center mt-2' style={{gap: 6}}>
          <span className='text-muted fs-7'>Полностью выключены:</span>
          {direction.disabledMethods.map((method) => (
            <span key={method} className='badge badge-light-secondary'>
              {method}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export const ClientRouting: React.FC<{token: string}> = ({token}) => {
  const [routing, setRouting] = useState<ClientRoutingData | null>(null)
  const [error, setError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setError('')

      try {
        const response = await getClientRouting(token)
        if (cancelled) return

        if (response.data.success && response.data.data) {
          setRouting(response.data.data)
        } else {
          setError(LOAD_ERROR)
        }
      } catch (e: unknown) {
        if (cancelled) return

        // an unknown client or a malformed token is this page's own failure text, not the backend's English sentence
        setError(getErrorText(e, LOAD_ERROR, {
          labels: { EXTERNAL_USER_NOT_FOUND: LOAD_ERROR, EXTERNAL_USER_TOKEN_INVALID: LOAD_ERROR },
        }))
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [token, reloadKey])

  if (error) {
    return (
      <div className='alert alert-danger d-flex align-items-center'>
        <KTIcon iconName='information-5' className='fs-2 me-3' />
        <span className='me-4'>{error}</span>
        <button
          type='button'
          className='btn btn-sm btn-light ms-auto'
          onClick={() => setReloadKey((v) => v + 1)}
        >
          Повторить
        </button>
      </div>
    )
  }

  if (!routing) {
    return (
      <div className='d-flex align-items-center text-muted' style={{minHeight: 120}}>
        <span className='spinner-border spinner-border-sm me-3' />
        Загрузка маршрутизации...
      </div>
    )
  }

  const {client, directions} = routing

  return (
    <div>
      <div className='d-flex align-items-center flex-wrap mb-5' style={{gap: 8}}>
        <span className='text-muted fs-7'>Флаг:</span>
        <FlagBadge flag={client.flag} />
        <span className='text-muted fs-7 ms-3'>
          трафик: {client.trafficType === 'secondary' ? 'повторный' : 'первичный'} · мерчант:{' '}
          {client.merchantTitle}
        </span>
      </div>

      {client.boundRequisites > 0 && (
        <div className='alert alert-info py-2 px-3 fs-7'>
          У клиента {client.boundRequisites}{' '}
          {pluralize(
            client.boundRequisites,
            'привязанный реквизит',
            'привязанных реквизита',
            'привязанных реквизитов'
          )}
          : когда цепочка доходит до своих касс, сначала пробуются они, и только если не подошли —
          обычная выдача по флагу.
        </div>
      )}

      {directions.map((direction) => (
        <DirectionBlock key={direction.direction} direction={direction} flag={client.flag} />
      ))}

      <div className='text-muted fs-8'>
        Список касс — это конфигурация, а не гарантия выдачи: реальный реквизит дополнительно
        фильтруется своими лимитами, бронями, валютой и порогами суммы.
      </div>
    </div>
  )
}
